import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import "@/i18n";
import App from "@/App";

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);

// PWA service worker registration. Only runs in production builds to keep
// dev cycles fast (CRA's dev server doesn't ship a SW anyway).
//
// Update strategy (fixes the recurring ERR_CACHE_MISS / stale-token issue):
//   1. When a new SW is `installed` while a controller already exists,
//      it means a fresh version is waiting → we expose the worker on
//      `window.__DM_WAITING_WORKER__` and fire a `dm:sw-waiting` event so
//      the React `PWAUpdateBanner` can prompt the user.
//   2. When the user clicks "Recharger", the banner posts SKIP_WAITING to
//      the waiting worker; the browser then swaps controllers and our
//      `controllerchange` listener force-reloads the page exactly once.
//   3. Registration is polled every 30 min so long-lived tabs pick up
//      new deploys without needing a manual refresh.
if ('serviceWorker' in navigator && process.env.NODE_ENV === 'production') {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/service-worker.js', { scope: '/' })
      .then((registration) => {
        const notifyWaiting = (worker) => {
          if (!worker) return;
          window.__DM_WAITING_WORKER__ = worker;
          window.dispatchEvent(new CustomEvent('dm:sw-waiting'));
        };

        // Case A — a waiting worker is already present at load time
        // (user opened the tab after a deploy but before reloading).
        if (registration.waiting && navigator.serviceWorker.controller) {
          notifyWaiting(registration.waiting);
        }

        // Case B — a new worker is being installed while the tab is open.
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (!newWorker) return;
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              notifyWaiting(newWorker);
            }
          });
        });

        // Reload the page exactly once when the new worker takes control,
        // so the freshly-cached bundles are used immediately.
        let refreshing = false;
        navigator.serviceWorker.addEventListener('controllerchange', () => {
          if (refreshing) return;
          refreshing = true;
          window.location.reload();
        });

        // Periodic update check for long-lived tabs (30 min).
        setInterval(() => {
          registration.update().catch(() => {});
        }, 30 * 60 * 1000);
      })
      .catch((err) => console.warn('[pwa] SW registration failed:', err));
  });
}
